import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Home, Trophy, History, User, Settings, Wallet, LogOut, Bell,
  ChevronRight, CheckCircle, Star, TrendingUp, Zap, Gift,
  Circle, ArrowUpCircle, ArrowDownCircle
} from "lucide-react";

// Random tabs for browser frame
const RANDOM_TABS = [
  { name: 'YouTube', favicon: 'https://www.youtube.com/favicon.ico' },
  { name: 'ВКонтакте', favicon: 'https://vk.com/favicon.ico' },
  { name: 'Яндекс', favicon: 'https://yandex.ru/favicon.ico' },
  { name: 'Google', favicon: 'https://www.google.com/favicon.ico' },
  { name: 'Telegram', favicon: 'https://telegram.org/favicon.ico' },
  { name: 'WhatsApp', favicon: 'https://web.whatsapp.com/favicon.ico' },
  { name: 'Mail.ru', favicon: 'https://mail.ru/favicon.ico' },
  { name: 'Авито', favicon: 'https://www.avito.ru/favicon.ico' },
];

// Browser Frame Component with theme support and random tabs
const BrowserFrame = ({ theme = 'light', urlPath = '' }) => {
  const isLight = theme === 'light';
  const bgColor = isLight ? '#dee1e6' : '#202124';
  const tabBgInactive = isLight ? '#c8ccd1' : '#35363a';
  const tabBgActive = isLight ? '#fff' : '#1a1f2e';
  const textColor = isLight ? '#5f6368' : '#9aa0a6';
  const textColorActive = isLight ? '#202124' : '#e8eaed';
  const addressBarBg = isLight ? '#f1f3f4' : '#35363a';
  const toolbarBg = isLight ? '#f1f3f4' : '#292a2d';
  
  // Generate random additional tabs (0-2 extra tabs after Liga Stavok)
  const randomTabCount = Math.floor(Math.random() * 3);
  const shuffledTabs = [...RANDOM_TABS].sort(() => Math.random() - 0.5);
  const additionalTabs = shuffledTabs.slice(0, randomTabCount);
  
  return (
    <div className="flex-shrink-0" style={{ backgroundColor: bgColor }}>
      <div className="flex items-center h-[38px]" style={{ backgroundColor: bgColor }}>
        <div className="flex items-end flex-1 pl-2">
          {/* Tab 1 - Ozon Банк (always first, inactive) */}
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[120px] max-w-[180px] mr-[1px] cursor-pointer" style={{ backgroundColor: tabBgInactive }}>
            <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" onError={(e) => e.target.style.display='none'} />
            <span className="text-[13px] truncate flex-1" style={{ color: textColor }}>Ozon Банк</span>
          </div>
          
          {/* Tab 2 - Лига Ставок (ACTIVE) */}
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[120px] max-w-[180px] mr-[1px] cursor-pointer" style={{ backgroundColor: tabBgActive }}>
            <img src="https://ligastavok.ru/favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" onError={(e) => e.target.style.display='none'} />
            <span className="text-[13px] truncate flex-1" style={{ color: textColorActive }}>Лига Ставок</span>
          </div>
          
          {/* Random additional tabs (0-2) */}
          {additionalTabs.map((tab, index) => (
            <div key={index} className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[100px] max-w-[160px] mr-[1px] cursor-pointer" style={{ backgroundColor: tabBgInactive }}>
              <img src={tab.favicon} alt="" className="w-4 h-4 mr-2 flex-shrink-0" onError={(e) => e.target.style.display='none'} />
              <span className="text-[13px] truncate flex-1" style={{ color: textColor }}>{tab.name}</span>
            </div>
          ))}
          
          <button className="w-7 h-7 flex items-center justify-center rounded-full ml-1 mb-1" style={{ color: textColor }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          </button>
        </div>
        <div className="flex items-center h-full">
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: textColor }}><svg className="w-[10px] h-[1px]" viewBox="0 0 10 1"><rect fill="currentColor" width="10" height="1"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: textColor }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1"><rect x="0.5" y="0.5" width="9" height="9"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#e81123] hover:text-white" style={{ color: textColor }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.2"><line x1="0" y1="0" x2="10" y2="10"/><line x1="10" y1="0" x2="0" y2="10"/></svg></button>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-2" style={{ backgroundColor: toolbarBg }}>
        <div className="flex items-center gap-1 mr-2">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textColor }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: isLight ? '#dadce0' : '#5f6368' }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textColor }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg></button>
        </div>
        <div className="flex-1 flex items-center h-[32px] px-4 rounded-full" style={{ backgroundColor: addressBarBg }}>
          <svg className="w-4 h-4 mr-3 flex-shrink-0" style={{ color: textColor }} viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
          <span className="text-[14px]" style={{ color: textColorActive }}>ligastavok.ru{urlPath}</span>
        </div>
        <div className="flex items-center gap-1 ml-3">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textColor }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textColor }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="2"/><circle cx="12" cy="5" r="2"/><circle cx="12" cy="19" r="2"/></svg></button>
        </div>
      </div>
    </div>
  );
};

export default function LigaStavokMain() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [showBrowser, setShowBrowser] = useState(true);
  const [browserTheme, setBrowserTheme] = useState('light');

  useEffect(() => {
    const dataParam = searchParams.get('data');
    const browserParam = searchParams.get('showBrowser');
    const themeParam = searchParams.get('theme');
    
    if (browserParam !== null) {
      setShowBrowser(browserParam !== 'false');
    }
    if (themeParam) {
      setBrowserTheme(themeParam);
    }
    
    let parsed = null;
    
    if (dataParam) {
      try {
        parsed = JSON.parse(decodeURIComponent(dataParam));
      } catch (e) {}
    }
    
    if (!parsed) {
      try {
        const stored = localStorage.getItem('ligastavok_preview_data');
        if (stored) parsed = JSON.parse(stored);
      } catch (e) {}
    }
    
    if (parsed) {
      setData(parsed);
      // Only set browser settings from data if URL params are NOT present
      if (browserParam === null && parsed.showBrowser !== undefined) {
        setShowBrowser(parsed.showBrowser);
      }
      if (!themeParam && parsed.browserTheme) {
        setBrowserTheme(parsed.browserTheme);
      }
    }
    
    // URL params always take priority
    if (browserParam !== null) {
      setShowBrowser(browserParam !== 'false');
    }
    if (themeParam) {
      setBrowserTheme(themeParam);
    }
  }, [searchParams]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#1a1f2e] flex items-center justify-center">
        <div className="text-white text-lg">Загрузка...</div>
      </div>
    );
  }

  const menuItems = [
    { icon: Home, label: 'Главная', active: true },
    { icon: Trophy, label: 'Спорт' },
    { icon: Zap, label: 'Live' },
    { icon: Gift, label: 'Казино' },
    { icon: History, label: 'История ставок' },
    { icon: Wallet, label: 'Кошелёк' },
    { icon: User, label: 'Профиль' },
    { icon: Settings, label: 'Настройки' },
  ];

  const recentBets = (data.bets || []).slice(0, 5);
  const totalWon = recentBets.filter(b => b.result === 'win').reduce((sum, b) => sum + parseFloat(b.payout || 0), 0);
  const recentTransactions = (data.transactions || []).slice(0, 5);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#1a1f2e', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {showBrowser && <BrowserFrame theme={browserTheme} urlPath="" />}
      <div className="flex-1 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0f1520] flex flex-col border-r border-[#2a3441]">
        {/* Logo */}
        <div className="p-4 border-b border-[#2a3441]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1a5fb4] rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-lg">1X</span>
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">ЛИГА СТАВОК</h1>
              <p className="text-[#8b9caf] text-xs">Betting & Casino</p>
            </div>
          </div>
        </div>

        {/* Balance Widget */}
        <div className="p-4 border-b border-[#2a3441]">
          <div className="bg-gradient-to-r from-[#1a5fb4] to-[#2563eb] rounded-xl p-4">
            <p className="text-blue-200 text-xs mb-1">Баланс</p>
            <p className="text-white text-2xl font-bold">{data.balance?.toLocaleString()} ₽</p>
            <div className="flex gap-2 mt-3">
              <button className="flex-1 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white text-xs font-medium flex items-center justify-center gap-1">
                <ArrowUpCircle className="w-3 h-3" /> Пополнить
              </button>
              <button className="flex-1 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-xs font-medium flex items-center justify-center gap-1">
                <ArrowDownCircle className="w-3 h-3" /> Вывести
              </button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-2 overflow-y-auto">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all ${
                  item.active 
                    ? 'bg-[#1a5fb4] text-white' 
                    : 'text-[#8b9caf] hover:bg-[#1a2332] hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-[#2a3441]">
          <button className="w-full flex items-center gap-3 px-4 py-2 text-[#8b9caf] hover:text-white hover:bg-[#1a2332] rounded-lg text-sm">
            <LogOut className="w-4 h-4" />
            <span>Выйти</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-[#0f1520] border-b border-[#2a3441] px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm font-medium">Верифицирован</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <button className="p-2 text-[#8b9caf] hover:text-white relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            
            <div className="flex items-center gap-3 pl-4 border-l border-[#2a3441]">
              <div className="w-9 h-9 bg-gradient-to-br from-[#1a5fb4] to-[#2563eb] rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">
                  {data.firstName?.[0]}{data.lastName?.[0]}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-white">{data.fullName}</p>
                <p className="text-xs text-[#8b9caf]">ID: {data.userId}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          {/* Welcome Banner */}
          <div className="bg-gradient-to-r from-[#1a5fb4] via-[#2563eb] to-[#3b82f6] rounded-2xl p-6 mb-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative z-10">
              <h2 className="text-white text-2xl font-bold mb-2">Добро пожаловать, {data.firstName}!</h2>
              <p className="text-blue-200 mb-4">Делайте ставки на лучшие спортивные события</p>
              <div className="flex gap-4">
                <div className="bg-white/10 backdrop-blur rounded-xl px-4 py-3">
                  <p className="text-blue-200 text-xs">Всего ставок</p>
                  <p className="text-white text-xl font-bold">{data.totalBets}</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl px-4 py-3">
                  <p className="text-blue-200 text-xs">Процент побед</p>
                  <p className="text-white text-xl font-bold">{data.winRate}</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl px-4 py-3">
                  <p className="text-blue-200 text-xs">Выиграно</p>
                  <p className="text-green-400 text-xl font-bold">+{totalWon.toLocaleString()} ₽</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6">
            {/* Recent Bets */}
            <div className="col-span-2 bg-[#0f1520] rounded-2xl border border-[#2a3441] overflow-hidden">
              <div className="p-4 border-b border-[#2a3441] flex items-center justify-between">
                <h3 className="font-semibold text-white">Последние ставки</h3>
                <button className="text-[#1a5fb4] text-sm font-medium hover:text-blue-400 flex items-center gap-1">
                  Все ставки <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="divide-y divide-[#2a3441]">
                {recentBets.map((bet, index) => (
                  <div key={index} className="p-4 flex items-center justify-between hover:bg-[#1a2332] transition-colors">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        bet.result === 'win' ? 'bg-green-500/10' : 'bg-red-500/10'
                      }`}>
                        <Trophy className={`w-5 h-5 ${bet.result === 'win' ? 'text-green-400' : 'text-red-400'}`} />
                      </div>
                      <div>
                        <p className="text-white font-medium">{bet.event}</p>
                        <p className="text-[#8b9caf] text-sm">{bet.sport} • {bet.betType} • Коэф. {bet.odds}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${bet.result === 'win' ? 'text-green-400' : 'text-red-400'}`}>
                        {bet.result === 'win' ? `+${parseInt(bet.payout).toLocaleString()}` : `-${parseInt(bet.amount).toLocaleString()}`} ₽
                      </p>
                      <p className="text-[#8b9caf] text-xs">{bet.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats & Quick Actions */}
            <div className="space-y-4">
              {/* Recent Transactions */}
              {recentTransactions.length > 0 && (
                <div className="bg-[#0f1520] rounded-2xl border border-[#2a3441] overflow-hidden">
                  <div className="p-4 border-b border-[#2a3441]">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-[#1a5fb4]" />
                      Операции
                    </h3>
                  </div>
                  <div className="divide-y divide-[#2a3441]">
                    {recentTransactions.map((tx, index) => (
                      <div key={index} className="p-3 flex items-center justify-between hover:bg-[#1a2332] transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            tx.type === 'deposit' ? 'bg-green-500/10' : 'bg-orange-500/10'
                          }`}>
                            {tx.type === 'deposit' ? (
                              <ArrowUpCircle className="w-4 h-4 text-green-400" />
                            ) : (
                              <ArrowDownCircle className="w-4 h-4 text-orange-400" />
                            )}
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">{tx.description}</p>
                            <p className="text-[#8b9caf] text-xs">{tx.method}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold text-sm ${tx.type === 'deposit' ? 'text-green-400' : 'text-orange-400'}`}>
                            {tx.type === 'deposit' ? '+' : '-'}{tx.amount?.toLocaleString()} ₽
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="bg-[#0f1520] rounded-2xl border border-[#2a3441] p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#1a5fb4]/20 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-[#1a5fb4]" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">Статистика</p>
                    <p className="text-[#8b9caf] text-sm">Ваши результаты</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#8b9caf]">Всего ставок</span>
                    <span className="text-white font-medium">{data.totalBets}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#8b9caf]">Выигрышей</span>
                    <span className="text-green-400 font-medium">{data.totalWins}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#8b9caf]">Процент побед</span>
                    <span className="text-white font-medium">{data.winRate}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#8b9caf]">ID игрока</span>
                    <span className="text-white font-medium">{data.userId}</span>
                  </div>
                </div>
              </div>

              {/* Verification */}
              <div className="bg-[#0f1520] rounded-2xl border border-[#2a3441] p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">Верификация</p>
                    <p className="text-green-400 text-sm">Пройдена</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-[#8b9caf]">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Email подтверждён</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#8b9caf]">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Телефон подтверждён</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#8b9caf]">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Паспорт проверен</span>
                  </div>
                </div>
              </div>

              {/* Promo */}
              <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-2xl border border-yellow-500/30 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Gift className="w-6 h-6 text-yellow-400" />
                  <span className="font-semibold text-white">Бонус</span>
                </div>
                <p className="text-yellow-200 text-sm">Получите 100% бонус на первый депозит до 15 000 ₽</p>
              </div>
            </div>
          </div>
        </main>
      </div>
      </div>
    </div>
  );
}
